// Suppliers register + create/edit (BUILD_SPEC.md §5.4, §11 "Registers with fast
// fuzzy search + filters"). Plan.md G20.
import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconBuildingOff, IconPlus } from '@tabler/icons-react';
import { ApiRequestError } from '../../api/client';
import { useCanOperate } from '../../auth/permissions';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import type { SupplierCreateInput } from '@zebra/core';
import { type SupplierRow, useCreateSupplier, useSuppliers, useUpdateSupplier } from './api';

interface SupplierFormValues {
  code: string;
  name: string;
  vatNumber: string;
  email: string;
  phone: string;
  paymentTerms: string;
  isActive: boolean;
}

function toFormValues(row?: SupplierRow): SupplierFormValues {
  return {
    code: row?.code ?? '',
    name: row?.name ?? '',
    vatNumber: row?.vatNumber ?? '',
    email: row?.email ?? '',
    phone: row?.phone ?? '',
    paymentTerms: row?.paymentTerms ?? '',
    isActive: row?.isActive ?? true,
  };
}

function toInput(values: SupplierFormValues): SupplierCreateInput {
  return {
    code: values.code.trim(),
    name: values.name.trim(),
    vatNumber: values.vatNumber.trim() || null,
    email: values.email.trim() || null,
    phone: values.phone.trim() || null,
    paymentTerms: values.paymentTerms.trim() || null,
    isActive: values.isActive,
  };
}

function SupplierFormModal({
  opened,
  onClose,
  editing,
}: {
  opened: boolean;
  onClose: () => void;
  editing: SupplierRow | null;
}) {
  const canOperate = useCanOperate();
  const create = useCreateSupplier();
  const update = useUpdateSupplier(editing?.id ?? '');
  const mutation = editing ? update : create;

  const form = useForm<SupplierFormValues>({
    initialValues: toFormValues(editing ?? undefined),
    validate: {
      code: (v) => (v.trim() ? null : 'Code is required'),
      name: (v) => (v.trim() ? null : 'Name is required'),
      email: (v) => (v.trim() === '' || /\S+@\S+\.\S+/.test(v) ? null : 'Invalid email'),
    },
  });

  function handleSubmit(values: SupplierFormValues) {
    mutation.mutate(toInput(values), { onSuccess: () => onClose() });
  }

  return (
    <Modal opened={opened} onClose={onClose} title={editing ? `Edit supplier ${editing.code}` : 'New supplier'} centered>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          {mutation.isError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {mutation.error instanceof ApiRequestError ? mutation.error.message : 'Failed to save supplier'}
            </Alert>
          )}
          <TextInput label="Code" required {...form.getInputProps('code')} />
          <TextInput label="Name" required {...form.getInputProps('name')} />
          <TextInput label="VAT number" {...form.getInputProps('vatNumber')} />
          <TextInput label="Email" type="email" {...form.getInputProps('email')} />
          <TextInput label="Phone" {...form.getInputProps('phone')} />
          <TextInput label="Payment terms" placeholder="e.g. 30 days" {...form.getInputProps('paymentTerms')} />
          <Switch label="Active" {...form.getInputProps('isActive', { type: 'checkbox' })} />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button type="submit" disabled={!canOperate} loading={mutation.isPending}>
                  {editing ? 'Save changes' : 'Create supplier'}
                </Button>
              </span>
            </Tooltip>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export function SupplierRegister() {
  const { data, isLoading, isError } = useSuppliers();
  const [modalOpened, setModalOpened] = useState(false);
  const [editing, setEditing] = useState<SupplierRow | null>(null);

  const rows = useMemo(() => data?.items ?? [], [data]);
  // Header/EmptyState CTA de-dupe (guidance-wave gap, 2026-08-26): on an empty
  // register the EmptyState below already offers "New supplier" as the sole
  // call-to-action — showing the same button in the page header too rendered
  // TWO "New supplier" buttons on a fresh tenant. Reuses the exact emptiness
  // condition the EmptyState branch checks.
  const showHeaderCreate = isLoading || rows.length > 0;

  function openCreate() {
    setEditing(null);
    setModalOpened(true);
  }

  function openEdit(row: SupplierRow) {
    setEditing(row);
    setModalOpened(true);
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Suppliers"
        description="Manage the suppliers you buy raw materials and consumables from."
        actions={
          showHeaderCreate ? (
            <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
              New supplier
            </Button>
          ) : undefined
        }
      />

      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load suppliers.
        </Alert>
      )}

      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={640}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Code</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>VAT number</Table.Th>
                <Table.Th>Contact</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{row.code}</Table.Td>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>{row.vatNumber ?? '—'}</Table.Td>
                  <Table.Td>
                    <Text size="sm">{row.email ?? '—'}</Text>
                    <Text size="xs" c="dimmed">
                      {row.phone ?? ''}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={row.isActive ? 'green' : 'gray'} variant="light">
                      {row.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Button variant="subtle" size="xs" onClick={() => openEdit(row)}>
                      Edit
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <EmptyState
                      icon={IconBuildingOff}
                      title="No suppliers yet"
                      hint="Add the suppliers you buy raw materials and consumables from before raising a purchase order."
                      action={
                        <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
                          New supplier
                        </Button>
                      }
                    />
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      {/* Keyed by editing target so `useForm`'s `initialValues` — captured once
          at mount, per @mantine/form — are re-derived on every "Edit" click
          instead of showing the FIRST row ever edited. */}
      <SupplierFormModal
        key={editing?.id ?? 'new'}
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        editing={editing}
      />
    </Stack>
  );
}
